import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { format, parseISO } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'

/**
 * Drop-in replacement for <input type="date">.
 *
 * Uses a self-contained fixed-positioned dropdown so the calendar is never
 * clipped by an ancestor overflow:hidden/auto (e.g. Sheet / Dialog scroll
 * containers). The position is calculated from the trigger's bounding rect
 * at open time and the calendar flips upward when there isn't enough space
 * below.
 *
 * **Inside a Radix Dialog / Sheet** the calendar is rendered *inline*
 * (absolute position within the trigger wrapper) rather than portaled.
 * Radix sets pointer-events:none on all portal siblings that are outside its
 * own content tree, which would make a portaled calendar non-interactive.
 *
 * - value: ISO YYYY-MM-DD string (or '' / null)
 * - onChange: receives ISO YYYY-MM-DD string ('' when cleared)
 */

/** Conservative height used to decide whether to flip the calendar upward. */
const CAL_DROPDOWN_H = 360

function isInsideRadixDialog(el: HTMLElement | null): boolean {
  let node = el
  while (node) {
    if (node.getAttribute('role') === 'dialog' && node.getAttribute('data-state') === 'open') {
      return true
    }
    node = node.parentElement
  }
  return false
}

export function DatePicker({
  value, onChange, disabled, required, placeholder = 'Pick a date', className,
  id, min, max,
  captionLayout = 'dropdown',
  fromYear, toYear,
}: {
  value: string | null | undefined
  onChange: (v: string) => void
  disabled?: boolean
  required?: boolean
  placeholder?: string
  className?: string
  id?: string
  /** ISO YYYY-MM-DD — inclusive lower bound. */
  min?: string | null
  /** ISO YYYY-MM-DD — inclusive upper bound. */
  max?: string | null
  /**
   * Calendar header layout.
   * ``'dropdown'`` (default) shows month + year select boxes for fast
   * navigation. ``'label'`` shows the plain "May 2026" static label.
   */
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years'
  /** Lower bound for the year dropdown (default: current year − 80). */
  fromYear?: number
  /** Upper bound for the year dropdown (default: current year + 5). */
  toYear?: number
}) {
  const [open, setOpen] = useState(false)
  // calPos is used for the portal (non-modal) path.
  const [calPos, setCalPos] = useState<React.CSSProperties>({})
  // flipUp and insideRadixDialog are computed once per open in openCalendar().
  const [flipUp, setFlipUp] = useState(false)
  const [insideModal, setInsideModal] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Separate ref for the calendar panel (needed for outside-click detection
  // regardless of whether the panel is portaled or inline).
  const dropdownRef = useRef<HTMLDivElement>(null)

  const date = value ? parseISO(value) : undefined
  const minDate = min ? parseISO(min) : undefined
  const maxDate = max ? parseISO(max) : undefined

  const thisYear = new Date().getFullYear()
  const fy = fromYear ?? thisYear - 80
  const ty = toYear ?? thisYear + 5
  const startMonth = new Date(fy, 0, 1)
  const endMonth = new Date(ty, 11, 31)

  /** Open the calendar, compute position, and detect modal context. */
  const openCalendar = useCallback(() => {
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - 8
    const spaceAbove = rect.top - 8
    const shouldFlip = spaceBelow < CAL_DROPDOWN_H && spaceAbove >= CAL_DROPDOWN_H
    setFlipUp(shouldFlip)
    // Portal path coords (used only when NOT inside a Radix modal).
    setCalPos({
      position: 'fixed',
      left: rect.left,
      minWidth: rect.width,
      ...(shouldFlip
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 9999,
    })
    // Determine rendering strategy: inside an open Radix Dialog/Sheet
    // pointer events are blocked on portal siblings, so render inline instead.
    setInsideModal(isInsideRadixDialog(el))
    setOpen(true)
  }, [])

  // Close on outside-click or Escape. Scroll closes only at the window level
  // (bubbling, no capture) so scrolling inside Sheet inner containers is fine.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      const insideTrigger = wrapperRef.current?.contains(target)
      const insideDropdown = dropdownRef.current?.contains(target)
      if (!insideTrigger && !insideDropdown) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll)   // bubbling only
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll)
    }
  }, [open])

  const calendarPanel = (
    <div
      ref={dropdownRef}
      className="rounded-lg bg-popover p-2.5 shadow-md ring-1 ring-foreground/10"
      style={insideModal ? {
        // Inline path: absolute within the position:relative wrapper.
        // Radix modal blocks pointer events on portaled siblings so we
        // stay inside the dialog's DOM tree.
        position: 'absolute',
        left: 0,
        zIndex: 50,
        ...(flipUp
          ? { bottom: 'calc(100% + 4px)' }
          : { top: 'calc(100% + 4px)' }),
      } : calPos}
    >
      <Calendar
        mode="single"
        selected={date}
        defaultMonth={date}
        captionLayout={captionLayout}
        startMonth={startMonth}
        endMonth={endMonth}
        disabled={(d) => {
          if (minDate && d < minDate) return true
          if (maxDate && d > maxDate) return true
          return false
        }}
        onSelect={(d) => {
          if (d) {
            const yyyy = d.getFullYear()
            const mm = String(d.getMonth() + 1).padStart(2, '0')
            const dd = String(d.getDate()).padStart(2, '0')
            onChange(`${yyyy}-${mm}-${dd}`)
          } else {
            onChange('')
          }
          setOpen(false)
        }}
      />
    </div>
  )

  return (
    <div ref={wrapperRef} className="relative inline-block w-full">
      <Button
        id={id}
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        className={cn(
          'w-full justify-start text-left font-normal h-10 px-3',
          !date && 'text-muted-foreground',
          className,
        )}
      >
        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
        {date ? format(date, 'PPP') : <span>{placeholder}</span>}
        {required && !date && <span className="ml-auto text-destructive">*</span>}
      </Button>

      {open && (insideModal
        ? calendarPanel                                   // inline — inside Radix Sheet/Dialog
        : createPortal(calendarPanel, document.body)      // portaled — outside modal
      )}
    </div>
  )
}

/**
 * Drop-in replacement for <input type="datetime-local">.
 * Uses the same fixed-position technique as DatePicker.
 * - value: ISO datetime string (or '' / null)
 * - onChange: receives ISO datetime string ('' when cleared)
 */
export function DateTimePicker({
  value, onChange, disabled, required, placeholder = 'Pick a date & time', className,
  id,
}: {
  value: string | null | undefined
  onChange: (v: string) => void
  disabled?: boolean
  required?: boolean
  placeholder?: string
  className?: string
  id?: string
}) {
  const [open, setOpen] = useState(false)
  const [calPos, setCalPos] = useState<React.CSSProperties>({})
  const wrapperRef = useRef<HTMLDivElement>(null)

  const date = value ? new Date(value) : undefined
  const timeStr = date
    ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    : '09:00'

  const emit = (d: Date | undefined, hhmm: string) => {
    if (!d) return onChange('')
    const [h, m] = hhmm.split(':').map(Number)
    const out = new Date(d)
    out.setHours(h || 0, m || 0, 0, 0)
    onChange(out.toISOString())
  }

  const openCalendar = useCallback(() => {
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - 8
    const spaceAbove = rect.top - 8
    const flipUp = spaceBelow < CAL_DROPDOWN_H && spaceAbove >= CAL_DROPDOWN_H
    setCalPos({
      position: 'fixed',
      left: rect.left,
      minWidth: rect.width,
      ...(flipUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 200,
    })
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  return (
    <div ref={wrapperRef} className="relative inline-block w-full">
      <Button
        id={id}
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        className={cn(
          'w-full justify-start text-left font-normal h-10 px-3',
          !date && 'text-muted-foreground',
          className,
        )}
      >
        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
        {date ? format(date, 'PPP p') : <span>{placeholder}</span>}
        {required && !date && <span className="ml-auto text-destructive">*</span>}
      </Button>

      {open && (
        <div
          className="rounded-lg bg-popover shadow-md ring-1 ring-foreground/10"
          style={calPos}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => emit(d, timeStr)}
          />
          <div className="border-t p-3 flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <input
              type="time"
              value={timeStr}
              onChange={(e) => emit(date, e.target.value)}
              className="flex-1 bg-background border border-input rounded px-2 py-1 text-sm"
            />
            <Button type="button" size="sm" onClick={() => setOpen(false)}>OK</Button>
          </div>
        </div>
      )}
    </div>
  )
}
